// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

import { MutationTree } from 'vuex';
import i18n from '@/i18n';

const namespaced = (val: string) => `info/${val}`;

export const mutationTypes = Object.freeze({
    ADD_BANNER: namespaced('addBanner'),
    ADD_MODAL: namespaced('addModal'),
    ADD_TOAST: namespaced('addToast'),
    REMOVE_BANNER: namespaced('removeBanner'),
    REFRESH_BANNERS: namespaced('refreshBanners'),
    REMOVE_MODAL: namespaced('removeModal'),
    REMOVE_TOAST: namespaced('removeToast'),
});

class Link {
    id;
    text;
    originalText;
    link;
    constructor(id: string, text: string, onClickFunc: () => void) {
        this.id = id;
        this.text = i18n.global.t(text);
        this.originalText = text;
        this.link = onClickFunc;
    }

    refreshText() {
        this.text = i18n.global.t(this.originalText);
    }
}

export class Banner {
    id: string;
    color: string;
    icon: string;
    text: string;
    originalText: string;
    links: Array<any>;

    constructor(id: string, color: string, icon: string, text: string, links: any[] = []) {
        this.id = id;
        this.color = color;
        this.icon = icon;
        this.text = i18n.global.t(text);
        this.originalText = text;
        this.links = links.map((link) => new Link(link.id, link.text, link.link));
    }

    refreshText() {
        this.text = i18n.global.t(this.originalText);
    }
}

export class Toast {
    show: boolean;
    color: string | null;
    icon: string | null;
    text: string | null;
    timer: number;
    links: any[];

    constructor() {
        this.show = false;
        this.color = null;
        this.icon = null;
        this.text = null;
        this.timer = 0;
        this.links = [];
    }
}

export class State {
    banners: Array<Banner>;
    modal: any;
    toast: Toast;

    constructor() {
        this.banners = [];
        this.modal = {};
        this.toast = new Toast();
    }
}

export const mutations: MutationTree<State> = {
    addBanner(state, bannerObject) {
        const newBanner = new Banner(
            bannerObject.id,
            bannerObject.color,
            bannerObject.icon,
            bannerObject.text,
            bannerObject.links
        );
        const duplicate = state.banners.find(
            (banner) => banner.id === newBanner.id
        );
        if (duplicate) {
            const location = state.banners.findIndex(
                (banner) => banner.id === newBanner.id
            );
            state.banners.splice(location, 1);
        }
        state.banners.push(newBanner);
    },
    addModal(state, modalObject) {
        // @ts-ignore
        document.querySelector('html').style.overflowY = 'hidden';
        state.modal = modalObject;
    },
    addToast(state, toastObject) {
        state.toast.show = true;
        state.toast.color = toastObject.color;
        state.toast.icon = toastObject.icon;
        state.toast.text = toastObject.text;
        state.toast.timer = toastObject.timer;
        state.toast.links = toastObject.links;
    },
    removeBanner(state, bannerID) {
        const location = state.banners.findIndex(
            (banner) => banner.id === bannerID
        );
        state.banners.splice(location, 1);
    },
    refreshBanners(state) {
        state.banners.forEach((banner) => {
            banner.refreshText();
            banner.links.forEach((link) => {
                link.refreshText();
            });
        });
    },
    removeModal(state) {
        // @ts-ignore
        document.querySelector('html').style.overflowY = 'auto';
        state.modal = {};
    },
    removeToast(state) {
        state.toast = {
            show: false,
            color: null,
            icon: null,
            text: null,
            timer: 0,
            links: [],
        };
    },
};

export default {
    namespaced: true,
    state: new State(),
    mutations,
};
